import { ColorModeContext, useMode } from './theme'
import { CssBaseline, ThemeProvider } from '@mui/material'
import Dashboard from './scenes/dashboard'
import BookingProcess from './Plot & Booking/BookingProcess'
import HoldBookings from './Plot & Booking/HoldBookings'
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom'
import ManageInstallments from './Installment_Payment/ManageInstallments'
import OverduePayments from './Installment_Payment/OverduePayments'
import { SnackbarProvider } from 'notistack'
import Login from './Auth/Login'
import ProtectedRoute from './Auth/ProtectedRoute'
import LayoutWithSidebarTopbar from './scenes/global/sidebar/LayoutWithSidebarTopbar'
import UserManagement from './Auth/Register'
import { useDispatch, useSelector } from 'react-redux'
import TransactionCreate from './Expense/TransactionCreate'
import {
  logoutUser,
  updateLastActivity,
  checkActiveUser,
  bootstrapAuth
} from './store/slices/authSlice'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PaidInstallments from './Installment_Payment/PaidInstallments'
import BookingDetailsTable from './Plot & Booking/BookingDetailsTable'
// import PaymentsExpense from "./Expense/PaymentsExpense";
import ExpenseIncomeTrack from './Expense/ExpenseIncomeTrack'
import ExpenseDirection from './Expense/ExpenseDirection'
import IncomeDirection from './Expense/IncomeDirection'
import SalaryExpense from './Expense/SalaryExpense'
import OtherExpense from './Expense/OtherExpense'
import FinancialSummaryPage from './Expense/FinancialSummaryPage'
import EmployeeCreation from './Auth/Users Management/EmployeeCreation'
import CustomerCreation from './Auth/Users Management/CustomerCreation'
import InvestorCreation from './Auth/Users Management/InvestorCreation'
import CreateSociety from './Society_Management/SocietyCreation'
import PlotCreation from './Society_Management/PlotCreation'
import PlotList from './Society_Management/PlotList'
import CashDayBookPage from './components/CashDayBook/CashDayBook'
import GeneralTransactionList from './General_Transactions/GeneralTransactionList'
import GeneralTransactionSummary from './General_Transactions/GeneralTransactionSummary'
import UserProfile from './Auth/UserProfile/index'
import CustomerTable from './Customer_Management/CustomerManagement'
import EmployeeTable from './Employee_Management/EmployeeManagement'
import InvestorTable from './Investor_Management/InvestorManagement'
import ResellManagement from './Plot_Resell/ResellManagement'
import ResellPlotsList from './Plot_Resell/ResellPlotsList'
import TransferPlotForm from './Plots_Transfer_Resell/TransferPlotForm'
import PlotTransferPage from './Plots_Transfer_Resell/PlotTransferPage'
import SalaryManagement from './Employee_Management/SalaryManagement'
import SalaryHistory from './Employee_Management/SalaryHistory'
import BookingDetailsInstallments from './Plot & Booking/BookingDetailsInstallments'
import AllBookingsPage from './Plot & Booking/AllBookingsPage'
import Notifications from './Alert/Notifications'
import MessagingDashboard from './Messages/MessagingDashboard'
import ScholarshipList from './Scholarship/ScholarshipList'
import AddScholarship from './Scholarship/AddScholarship'
import ShortlistedStudents from './Scholarship/ShortlistedStudents'
import ApprovedScholarships from './Scholarship/ApprovedScholarships'
import AccessManagement from './scenes/global/AccessManagement'
import SocietyMap from './scenes/Society_Map/Society_Map'
import SingleCustomer from './Customer_Management/SingleCustomer'
import SalaryAdvanceWithSnackbar from './Employee_Management/SalaryAdvance'
import LeadManagement from "./LeadGeneration/LeadManagement";

import CancelPlotDashboard from './CancelPlot/CancelPlotDashboard'
import PlotCancellationManagement from './CancelPlot/PlotCancellationManagement'
import ConcessionManagement from './Concession/ConcessionManagement'
import ApplyConcessionPage from './Concession/ApplyConcessionPage'
import ConcessionListPage from './Concession/ConcessionListPage'
import AllAgreements from './Agreement/AllAgreements'
import OverdueAgreements from './Agreement/OverdueAgreements'
import CommissionDealers from './Commission/CommissionDealers'

import { initSocket, disconnectSocket } from './socket' // Import socket utilities
import RealTimeNotification from './components/RealTimeNotification'

const INACTIVITY_LIMIT_MINUTES = 5
const INACTIVITY_CHECK_INTERVAL_MS = 600000

const App = () => {
  const [theme, colorMode] = useMode()
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isAuthenticated, lastActivity, activeUser, authChecked } = useSelector(
    state => state.auth
  )
  const [hasNavigated, setHasNavigated] = useState(false)

  // Initialize socket connection when authenticated
  useEffect(() => {
    // Only initialize socket if authenticated and there's a token
    if (isAuthenticated && sessionStorage.getItem('token')) {
      // Initialize socket connection
      initSocket()

      // Set up cleanup on component unmount
      return () => {
        disconnectSocket()
      }
    }
  }, [isAuthenticated])

  // Bootstrap auth once on app mount
  useEffect(() => {
    dispatch(bootstrapAuth())
  }, [dispatch])

  useEffect(() => {
    if (performance.navigation.type === 1) {
      sessionStorage.setItem('isReloading', 'true')
    }
    return () => {
      sessionStorage.removeItem('isReloading')
    }
  }, [])

  // Removed: forced logout on initial mount (caused unwanted API calls/race conditions)

  const handleActivity = useCallback(() => {
    dispatch(updateLastActivity())
  }, [dispatch])

  useEffect(() => {
    if (!isAuthenticated) return

    const checkInactivity = () => {
      const now = Date.now()
      const inactiveTime = (now - lastActivity) / 1000 / 60
      if (inactiveTime >= INACTIVITY_LIMIT_MINUTES) {
        dispatch(logoutUser())
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('lastPath')
      }
    }

    const activityEvents = ['mousemove', 'keydown', 'click']
    const throttledHandleActivity = throttle(handleActivity, 200)
    activityEvents.forEach(event =>
      window.addEventListener(event, throttledHandleActivity)
    )
    const inactivityTimer = setInterval(
      checkInactivity,
      INACTIVITY_CHECK_INTERVAL_MS
    )
    return () => {
      activityEvents.forEach(event =>
        window.removeEventListener(event, throttledHandleActivity)
      )
      clearInterval(inactivityTimer)
    }
  }, [isAuthenticated, lastActivity, dispatch, handleActivity])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAuthenticated) {
        sessionStorage.setItem('isTabClosing', 'true')
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isAuthenticated])

  const rolePaths = useMemo(
    () => ({
      Admin: '/',
      Employee: '/',
      Customer: '/installments-payment/paid-installments',
      Investor: '/alert/notifications'
    }),
    []
  )
  useEffect(() => {
    if (isAuthenticated && !hasNavigated) {
      setHasNavigated(true)
      const storedLastPath = sessionStorage.getItem('lastPath')
      if (!location.pathname || location.pathname === '/') {
        const defaultPath = activeUser?.role
          ? rolePaths[activeUser.role] || '/login'
          : '/login'

        navigate(storedLastPath || defaultPath, { replace: true })
      }
    }
  }, [
    isAuthenticated,
    activeUser,
    hasNavigated,
    rolePaths,
    navigate,
    location.pathname
  ])

  // Do not render routes until auth check finishes to avoid premature API calls
  if (!authChecked) {
    return null
  }

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
        >
          <div style={{ height: '100%', width: '100%' }}>
            {/* Real-time notification component */}
            {isAuthenticated && <RealTimeNotification />}
            <main>
              <Routes>
                {/* Login Route (No Sidebar or Topbar) */}
                <Route
                  path='/login'
                  element={isAuthenticated ? <Navigate to='/' /> : <Login />}
                />
                {/* Protected Routes (with Sidebar and Topbar) */}

                <Route
                  path='/'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      {isAuthenticated ? (
                        activeUser?.role === 'Customer' ||
                          activeUser?.role === 'Investor' ? (
                          <Navigate
                            to={
                              activeUser?.role === 'Customer'
                                ? '/installments-payment/paid-installments'
                                : '/alert/notifications'
                            }
                          />
                        ) : (
                          <LayoutWithSidebarTopbar component={<Dashboard />} />
                        )
                      ) : (
                        <Navigate to='/login' />
                      )}
                    </ProtectedRoute>
                  }
                />
                {/* User Create and Management */}
                <Route
                  path='/user-creation/all-users'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<UserManagement />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/user-creation/employee-creation'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<EmployeeCreation />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/user-creation/customer-creation'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<CustomerCreation />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/user-creation/investor-creation'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<InvestorCreation />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/user-creation/user-profile'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<UserProfile />} />
                    </ProtectedRoute>
                  }
                />
                {/* Plots and Booking */}
                <Route
                  path='/plots-booking/booking-process'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<BookingProcess />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/plots-booking/booking-full'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<BookingDetailsTable />}
                      />
                    </ProtectedRoute>
                  }
                />
                  <Route
                    path='/plots-booking/all-bookings'
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated}>
                        <LayoutWithSidebarTopbar
                          component={<AllBookingsPage />}
                        />
                      </ProtectedRoute>
                    }
                  />
                <Route
                  path='/plots-booking/booking-installments'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<BookingDetailsInstallments />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/plots-booking/hold-bookings'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<HoldBookings />} />
                    </ProtectedRoute>
                  }
                />

                {/* INSTALLMENTS AND PAY MANAGEMENT */}
                <Route
                  path='/installments-payment/manage-installments'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ManageInstallments />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/installments-payment/paid-installments'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<PaidInstallments />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/installments-payment/overdue-payments'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<OverduePayments />}
                      />
                    </ProtectedRoute>
                  }
                />
                {/* PLOTS TRANSFER AND RESELL MANAGEMENT */}
                <Route
                  path='/plot-transfer-resell/plot-resale-listings'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ResellPlotsList />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/plot-transfer-resell/plot-transfer-creation'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<TransferPlotForm />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/plot-transfer-resell/plot-transfer-page'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<PlotTransferPage />}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/plot-transfer-resell/transfer-plot-ownership'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ResellManagement />}
                      />
                    </ProtectedRoute>
                  }
                />
                {/* INCOME AND EXPENSE MANAGEMENT */}
                <Route
                  path='/income-expense/transaction'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ExpenseIncomeTrack />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/expense/transaction'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ExpenseDirection />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/income/transaction'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<IncomeDirection />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/create/transaction'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<TransactionCreate />}
                      />
                    </ProtectedRoute>
                  }
                />
                {/* <Route
                  path="/income-expense/payments-expense"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<PaymentsExpense />}
                      />
                    </ProtectedRoute>
                  }
                /> */}
                <Route
                  path='/income-expense/manage-salaries'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<SalaryExpense />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/income-expense/other-expense'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<OtherExpense />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/income-expense/financial-summary'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<FinancialSummaryPage />}
                      />
                    </ProtectedRoute>
                  }
                />
                {/* Housing Society MANAGEMENT */}
                <Route
                  path='/Housing-Society-Management/create-social-projects'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<CreateSociety />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/housing-society-management/plots-management'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<PlotCreation />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/housing-society-management/plots-list'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<PlotList />} />
                    </ProtectedRoute>
                  }
                />
                {/* Cash Day Book (Khata) */}
                <Route
                  path='/cash-day-book'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<CashDayBookPage />}
                      />
                    </ProtectedRoute>
                  }
                />

                {/* General Transaction MANAGEMENT */}
                <Route
                  path='/general-transaction'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<CashDayBookPage />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/general-trans-list'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<GeneralTransactionList />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/general-trans-summary'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<GeneralTransactionSummary />}
                      />
                    </ProtectedRoute>
                  }
                />
                {/* Customer Management */}
                <Route
                  path='/customer/information'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<CustomerTable />} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/customer/active-info'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<SingleCustomer />} />
                    </ProtectedRoute>
                  }
                />
                {/* /employee-management/manage-employee-info */}
                <Route
                  path='/employee-management/manage-employee-info'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<EmployeeTable />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/employee-management/salary_management'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<SalaryManagement />}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/employee-management/advance_salary'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<SalaryAdvanceWithSnackbar />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/employee-management/salary_history'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<SalaryHistory />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/investor-management/manage-investor-info'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<InvestorTable />} />
                    </ProtectedRoute>
                  }
                />
                {/* /alert/notifications */}
                <Route
                  path='/alert/notifications'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<Notifications />} />
                    </ProtectedRoute>
                  }
                />

                {/* /Scholarship */}
                <Route
                  path='/scholarship-management/shortlist'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ShortlistedStudents />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/scholarship-management/list'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ScholarshipList />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/scholarship-management/form'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<AddScholarship />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/scholarship-management/Approval'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ApprovedScholarships />}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/tabs-settings'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<AccessManagement />}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/society_map/map'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<SocietyMap />} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/messaging-dashboard'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<MessagingDashboard />}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/cancel_plot'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<CancelPlotDashboard />}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/cancel_plot_installments'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<PlotCancellationManagement />}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/concessions/apply'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ApplyConcessionPage />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/concessions/list'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<ConcessionListPage />}
                      />
                    </ProtectedRoute>
                  }
                />
                {/* Keep old route for backward compatibility - redirect to apply */}
                <Route
                  path='/concessions'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Navigate to='/concessions/apply' replace />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/agreements/all'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<AllAgreements />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/agreements/overdue'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<OverdueAgreements />}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/commission/dealers'
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<CommissionDealers />}
                      />
                    </ProtectedRoute>
                  }
                />
                {/* <Route
                  path="/cancel_plot"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar
                        component={<CancelPlotDashboard />}
                      />
                    </ProtectedRoute>
                  }
                /> */}

                <Route
                  path="/lead_generation"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<LeadManagement />} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/lead_generation/pending"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<LeadManagement />} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lead_generation/unassigned"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <LayoutWithSidebarTopbar component={<LeadManagement />} />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </main>
          </div>
        </SnackbarProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}
function throttle(func, limit) {
  let lastFunc
  let lastRan

  return function (...args) {
    const context = this
    if (!lastRan) {
      func.apply(context, args)
      lastRan = Date.now()
    } else {
      clearTimeout(lastFunc)
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args)
          lastRan = Date.now()
        }
      }, limit - (Date.now() - lastRan))
    }
  }
}
export default App
